import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminLogin = () => {
  const [email, setEmail] = useState('');  // No prefilled email (Security: B1)
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);  // A) Check if already authenticated
  const navigate = useNavigate();

  // A) Check if already authenticated - redirect to dashboard if valid token exists
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = localStorage.getItem('admin_access_token');
      if (!token) {
        setCheckingAuth(false);
        return;
      }

      try {
        // Verify token is still valid
        await axios.get('/api/admin/auth/verify', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Token valid - redirect to dashboard (prevents login→dashboard→login loops)
        console.log('[Admin Login] Valid token found, redirecting to dashboard');
        navigate('/admin/dashboard', { replace: true });
      } catch (err) {
        // Token invalid/expired - clear it and show login
        console.log('[Admin Login] Token invalid or expired, clearing');
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_user');
        setCheckingAuth(false);
      }
    };

    checkExistingAuth();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('[Admin Login] Starting login process...');

    try {
      const apiUrl = '/api/admin/auth/login';
      
      console.log('[Admin Login] Calling:', apiUrl);

      const response = await axios.post(apiUrl, {
        email,
        password,
        totp_code: totpCode || undefined  // Include 2FA code if provided
      }, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: false
      });

      console.log('[Admin Login] Response status:', response.status);

      const { access_token, user, force_password_change, requires_2fa, force_2fa_setup } = response.data;

      // Check if 2FA is required
      if (requires_2fa) {
        console.log('[Admin Login] 2FA required');
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      if (!access_token) {
        throw new Error('No access token received from server');
      }

      // Store token SYNCHRONOUSLY before any navigation
      localStorage.setItem('admin_access_token', access_token);
      localStorage.setItem('admin_user', JSON.stringify(user));
      console.log('[Admin Login] Token stored successfully');

      // Small delay to ensure localStorage is committed before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Redirect based on force_password_change (C) or force_2fa_setup
      if (force_password_change) {
        console.log('[Admin Login] Force password change required, redirecting');
        navigate('/admin/dashboard', { replace: true, state: { forcePasswordChange: true } });
        setTimeout(() => {
          if (window.location.pathname !== '/admin/change-password') {
            window.location.href = '/admin/change-password';
          }
        }, 200);
      } else if (force_2fa_setup) {
        console.log('[Admin Login] 2FA setup required, redirecting');
        navigate('/admin/setup-2fa', { replace: true });
        setTimeout(() => {
          if (window.location.pathname !== '/admin/setup-2fa') {
            window.location.href = '/admin/setup-2fa';
          }
        }, 200);
      } else {
        console.log('[Admin Login] Redirecting to dashboard');
        navigate('/admin/dashboard', { replace: true });
        setTimeout(() => {
          if (window.location.pathname !== '/admin/dashboard') {
            window.location.href = '/admin/dashboard';
          }
        }, 200);
      }
    } catch (err) {
      console.error('[Admin Login] Error:', err);
      
      let errorMessage;
      if (err.response) {
        const status = err.response.status;
        if (status === 401) {
          if (requires2FA) {
            errorMessage = 'Invalid 2FA code. Please try again.';
          } else {
            errorMessage = 'Invalid email or password';
          }
        } else if (status === 429) {
          errorMessage = 'Too many login attempts. Please try again in 10 minutes.';
        } else if (status === 403) {
          errorMessage = err.response.data?.detail || 'Admin access required. This account is not an administrator.';
        } else {
          errorMessage = err.response.data?.detail || `Server error: ${status}`;
        }
      } else if (err.request) {
        errorMessage = 'Network error: Could not connect to server.';
      } else {
        errorMessage = err.message || 'Login failed. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '1.25rem' }}>Checking authentication...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div style={{
        background: 'white',
        padding: '3rem',
        borderRadius: '1rem',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h1 style={{ marginBottom: '0.5rem', fontSize: '2rem', fontWeight: 'bold', textAlign: 'center' }}>
          Admin Portal
        </h1>
        <p style={{ marginBottom: '2rem', color: '#666', textAlign: 'center' }}>
          Mic Check Millionaire Back Office
        </p>

        {error && (
          <div style={{
            padding: '1rem',
            marginBottom: '1rem',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '0.5rem',
            color: '#c33'
          }}>
            {error}
          </div>
        )}

        {/* Hidden fields to trick Safari autofill away from real inputs */}
        <input type="text" name="fakeusernameremembered" style={{ display: 'none' }} tabIndex={-1} />
        <input type="password" name="fakepasswordremembered" style={{ display: 'none' }} tabIndex={-1} />
        
        <form onSubmit={handleLogin} autoComplete="off">
          {!requires2FA ? (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: '600' }}>Email</label>
                  {email && (
                    <button
                      type="button"
                      onClick={() => setEmail('')}
                      style={{ background: 'none', border: 'none', color: '#667eea', fontSize: '0.75rem', cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Email address"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-form-type="other"
                  data-lpignore="true"
                  name="admin-email-field"
                  id="admin-email-field"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.5rem',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: '600' }}>Password</label>
                  {password && (
                    <button
                      type="button"
                      onClick={() => setPassword('')}
                      style={{ background: 'none', border: 'none', color: '#667eea', fontSize: '0.75rem', cursor: 'pointer' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter password"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-form-type="other"
                  data-lpignore="true"
                  name="admin-password-field"
                  id="admin-password-field"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.5rem',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </>
          ) : (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ 
                background: '#f0f9ff', 
                padding: '1rem', 
                borderRadius: '0.5rem', 
                marginBottom: '1rem',
                borderLeft: '4px solid #667eea'
              }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#374151' }}>
                  🔐 Two-factor authentication is enabled on this account.
                  <br />Enter the 6-digit code from your authenticator app.
                </p>
              </div>
              <label style={{ fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>Authentication Code</label>
              <input
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                placeholder="000000"
                autoComplete="one-time-code"
                autoFocus
                style={{
                  width: '100%',
                  padding: '1rem',
                  border: '2px solid #667eea',
                  borderRadius: '0.5rem',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  letterSpacing: '0.5rem',
                  fontWeight: 'bold'
                }}
                maxLength={6}
              />
              <button
                type="button"
                onClick={() => {
                  setRequires2FA(false);
                  setTotpCode('');
                  setError('');
                }}
                style={{
                  marginTop: '0.75rem',
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  textDecoration: 'underline'
                }}
              >
                ← Back to login
              </button>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '1rem',
              background: loading ? '#999' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s'
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>

          {/* D) Forgot Password Link */}
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <a
              href="/admin/forgot-password"
              style={{
                color: '#667eea',
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
            >
              Forgot your password?
            </a>
          </div>
        </form>

        <div style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.875rem', color: '#999' }}>
          <p>Admin access only. Unauthorized access is prohibited.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
